// Authentication service for UniFirst SSO using OIDC
// Retrieves OIDC configuration from Azure Key Vault through environment variables

import OidcAuthService from './oidcAuth';
import { getActiveMockProfile } from '../utils/mockProfiles';

export interface AuthUser {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  claims: Array<{
    typ: string;
    val: string;
  }>;
  // Enriched profile from Microsoft Graph (populated after login)
  graphProfile?: {
    id?: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    jobTitle?: string;
    department?: string;
    companyName?: string;
    employeeId?: string;
    officeLocation?: string;
    city?: string;
    state?: string;
    country?: string;
    mobilePhone?: string;
    manager?: {
      id: string;
      displayName: string;
      email?: string;
      jobTitle?: string;
      department?: string;
    } | null;
    directReportsCount?: number;
    hasDirectReports?: boolean;
    photoUrl?: string | null;
    hasPhoto?: boolean;
    enrichedAt?: string;
  };
}

// Authentication provider type (OIDC only)
export type AuthProvider = 'oidc';

export interface ClientPrincipal {
  identityProvider: string;
  userId: string;
  userDetails: string;
  userRoles: string[];
  claims: Array<{
    typ: string;
    val: string;
  }>;
}

interface RBACPermissions {
  hasAdminAccess: boolean;
  hasFullAdminAccess: boolean;
  isSuperAdmin: boolean;
  agentAdminRoles: Array<{
    agentId: string;
    role: string;
    assignedAt: string;
  }>;
}

class AuthService {
  private static instance: AuthService;
  private currentUser: AuthUser | null = null;
  private cachedRBACPermissions: RBACPermissions | null = null;
  private rbacPermissionsExpiry: number = 0;
  private authProvider: AuthProvider;
  private oidcService: OidcAuthService;

  private constructor() {
    // Always use OIDC for UniFirst SSO
    this.authProvider = 'oidc';
    this.oidcService = OidcAuthService.getInstance();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Check if we're running in local development mode
  private isLocalDevelopment(): boolean {
    if (process.env.REACT_APP_FORCE_REAL_AUTH === 'true') {
      console.log('🔑 REACT_APP_FORCE_REAL_AUTH enabled - using real Azure AD authentication');
      return false;
    }

    return (
      process.env.NODE_ENV === 'development' ||
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.port === '3000'
    );
  }

  // Get mock user data for local development
  private getMockUser(): AuthUser {
    // FIRST: Check for active mock profile (from DevTestingPanel Mock Profiles section)
    const mockProfile = getActiveMockProfile();
    if (mockProfile) {
      console.log('🎭 [Auth] Using mock profile:', mockProfile.name, 'with roles:', mockProfile.roles);
      return {
        identityProvider: 'oidc',
        userId: 'mock-user-id',
        userDetails: `MockUser_${mockProfile.id}@unifirst.com`,
        userRoles: mockProfile.roles,
        claims: [
          { typ: 'name', val: `Mock User (${mockProfile.name})` },
          { typ: 'email', val: `MockUser_${mockProfile.id}@unifirst.com` },
          { typ: 'given_name', val: 'Mock' },
          { typ: 'family_name', val: 'User' },
          ...mockProfile.roles.filter(r => r !== 'authenticated').map(r => ({ typ: 'role', val: r }))
        ]
      };
    }
    
    // SECOND: Check for custom roles from DevTestingPanel (User Role Overrides section)
    const customRolesEnabled = localStorage.getItem('askaldo_dev_custom_roles_enabled') === 'true';
    
    if (customRolesEnabled) {
      try {
        const customRolesData = localStorage.getItem('askaldo_dev_custom_roles');
        if (customRolesData) {
          const customRoles = JSON.parse(customRolesData);
          const roles: string[] = ['authenticated'];
          
          // Map custom role flags to actual role strings
          if (customRoles.isSuperAdmin) roles.push('super-admin');
          if (customRoles.isAdmin) roles.push('admin');
          if (customRoles.isDeveloper) roles.push('developer');
          if (customRoles.isHrAdmin) roles.push('hr-admin');
          if (customRoles.isPwmsAdmin) roles.push('pwms-admin');
          if (customRoles.isProductAdmin) roles.push('product-admin');
          if (customRoles.isAldoAdmin) roles.push('aldo-admin');
          if (customRoles.isOwensboroAdmin) roles.push('owensboro2-admin');
          if (customRoles.isUnfDocsAdmin) roles.push('unfdocs-admin');
          if (customRoles.isOrientationAdmin) roles.push('orientation-admin');
          if (customRoles.isIrisAdmin) roles.push('iris-admin');
          if (customRoles.isServiceDeskAdmin) roles.push('service-desk-admin');
          if (customRoles.isDirectEmbroideryAdmin) roles.push('direct-embroidery-admin');
          
          console.log('🧪 [Auth] Using custom roles from DevTestingPanel:', roles);
          
          return {
            identityProvider: 'oidc',
            userId: 'mock-user-id',
            userDetails: 'Local_Developer@unifirst.com',
            userRoles: roles,
            claims: [
              { typ: 'name', val: 'Local Developer (Custom Roles)' },
              { typ: 'email', val: 'Local_Developer@unifirst.com' },
              { typ: 'given_name', val: 'Local' },
              { typ: 'family_name', val: 'Developer' },
              ...roles.filter(r => r !== 'authenticated').map(r => ({ typ: 'role', val: r }))
            ]
          };
        }
      } catch (e) {
        console.warn('Failed to parse custom roles:', e);
      }
    }
    
    // Default: Full admin access in development
    const mockAsAdmin = localStorage.getItem('mockAdminMode') !== 'false' && 
                       process.env.REACT_APP_MOCK_ADMIN !== 'false';
    
    // Give local dev full admin access including all agent-specific roles
    const adminRoles = mockAsAdmin ? [
      'authenticated', 
      'admin', 
      'super-admin',
      'product-admin',
      'hr-admin',
      'pwms-admin',
      'aldo-admin',
      'owensboro2-admin',
      'unfdocs-admin',
      'orientation-admin',
      'iris-admin',
      'service-desk-admin'
    ] : ['authenticated'];
    
    return {
      identityProvider: 'oidc',
      userId: 'mock-user-id',
      userDetails: 'Local_Developer@unifirst.com',
      userRoles: adminRoles,
      claims: [
        { typ: 'name', val: 'Local Developer' },
        { typ: 'email', val: 'Local_Developer@unifirst.com' },
        { typ: 'given_name', val: 'Local' },
        { typ: 'family_name', val: 'Developer' },
        ...(mockAsAdmin ? [{ typ: 'role', val: 'super-admin' }] : [])
      ]
    };
  }

  // Get current user information from OIDC provider
  async getCurrentUser(): Promise<AuthUser | null> {
    // FIRST: Check if DevTestingPanel custom roles are enabled (works even with real auth)
    const customRolesEnabled = localStorage.getItem('askaldo_dev_custom_roles_enabled') === 'true';
    const isLocalhost = window.location.hostname === 'localhost';
    const isLocalDev = this.isLocalDevelopment();
    
    console.log('🔍 [Auth] getCurrentUser check:', {
      customRolesEnabled,
      isLocalhost,
      isLocalDev,
      customRolesData: localStorage.getItem('askaldo_dev_custom_roles'),
      REACT_APP_FORCE_REAL_AUTH: process.env.REACT_APP_FORCE_REAL_AUTH
    });
    
    if (customRolesEnabled && (isLocalDev || isLocalhost)) {
      this.currentUser = this.getMockUser();
      console.log('🧪 [Auth] DevTestingPanel custom roles override active:', {
        email: this.currentUser.userDetails,
        roles: this.currentUser.userRoles
      });
      return this.currentUser;
    }
    
    // If running locally without custom roles, return default mock user data
    if (isLocalDev) {
      this.currentUser = this.getMockUser();
      console.log('🔧 [Auth] Local development detected, using mock user:', {
        email: this.currentUser.userDetails,
        roles: this.currentUser.userRoles
      });
      // RBAC permissions now come from JWT roles, no API call needed
      return this.currentUser;
    }

    // Always use OIDC for UniFirst SSO
    console.log('🔐 [Auth] Using real Azure AD authentication');
    const user = await this.getCurrentUserOidc();
    
    // RBAC permissions now come from JWT roles (user.userRoles)
    // No need to fetch from deprecated /api/admin/my-permissions endpoint
    
    return user;
  }

  // Get current user from OIDC provider
  private async getCurrentUserOidc(): Promise<AuthUser | null> {
    try {
      const oidcUser = await this.oidcService.getCurrentUser();
      
      if (oidcUser && !oidcUser.expired) {
        this.currentUser = this.oidcService.convertToAuthUser(oidcUser);
        return this.currentUser;
      }
      
      this.currentUser = null;
      return null;
    } catch (error) {
      console.error('Error fetching OIDC user information:', error);
      this.currentUser = null;
      return null;
    }
  }


  // Check if user is authenticated
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  }

  // Get user display name from claims (simplified to use email)
  getUserDisplayName(user: AuthUser): string {
    if (!user) {
      return 'Unknown User';
    }

    // First, try to get the email and use it as display name
    const email = this.getUserEmail(user);
    if (email) {
      // Only log in development mode to reduce console noise
      if (process.env.NODE_ENV === 'development') {
        console.log('Using email as display name:', email);
      }
      return email;
    }

    // Fallback: try userDetails if available
    if (user.userDetails && user.userDetails.trim()) {
      return user.userDetails.trim();
    }
    
    return 'Unknown User';
  }

  // Get user email from claims
  getUserEmail(user: AuthUser): string {
    if (!user) return '';
    
    // Try multiple possible email claim types (if claims exist)
    if (user.claims && Array.isArray(user.claims)) {
      const possibleEmailClaims = [
        'email',                                                             // Standard email claim
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress', // Email claim URI
        'upn',                                                               // User Principal Name (often email)
        'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn',         // UPN claim URI
        'preferred_username',                                                // Sometimes contains email
        'unique_name'                                                        // Sometimes contains email
      ];
      
      for (const claimType of possibleEmailClaims) {
        const claim = user.claims.find(c => c.typ === claimType);
        if (claim && claim.val && claim.val.includes('@')) {
          // Only log in development mode to reduce console noise
          if (process.env.NODE_ENV === 'development') {
            // console.log(`Found email in claim '${claimType}':`, claim.val);
          }
          return claim.val.trim();
        }
      }
    }
    
    // Fallback to userDetails if it looks like an email
    if (user.userDetails && user.userDetails.includes('@')) {
      return user.userDetails.trim();
    }
    
    return '';
  }

  // Get user avatar initials
  getUserAvatar(user: AuthUser): string {
    const displayName = this.getUserDisplayName(user);
    
    // Don't use "Unknown User" for avatar generation
    if (displayName === 'Unknown User') {
      // Try to get initials from email if available
      const email = this.getUserEmail(user);
      if (email) {
        const emailName = email.split('@')[0];
        const emailWords = emailName.split(/[._-]/);
        if (emailWords.length >= 2) {
          return (emailWords[0][0] + emailWords[1][0]).toUpperCase();
        } else if (emailWords[0].length > 0) {
          return emailWords[0][0].toUpperCase();
        }
      }
      return 'U';
    }
    
    const words = displayName.split(' ');
    
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1 && words[0].length > 0) {
      return words[0][0].toUpperCase();
    }
    
    return 'U';
  }

  // Check if user has specific role
  hasRole(user: AuthUser, role: string): boolean {
    return user?.userRoles?.includes(role) || false;
  }

  // Check if current user is admin
  isAdmin(): boolean {
    return this.hasRole(this.currentUser!, 'admin');
  }

  // Check if current user has admin access (includes future role types)
  hasAdminAccess(): boolean {
    if (!this.currentUser) {
      console.log('🔧 [Auth] hasAdminAccess: No current user');
      return false;
    }
    
    // DEBUG: Log all user roles to help diagnose role issues across environments
    console.log('🔐 [Auth] hasAdminAccess - User roles check:', {
      allRoles: this.currentUser.userRoles,
      email: this.currentUser.userDetails,
      claims: this.currentUser.claims?.filter(c => c.typ.toLowerCase().includes('role')),
      identityProvider: this.currentUser.identityProvider
    });
    
    // First check traditional roles for backward compatibility
    // NOTE: Role names are case-sensitive and use kebab-case (e.g., "super-admin" not "Super Admin")
    const hasTraditionalRole = this.hasRole(this.currentUser, 'admin') || 
           this.hasRole(this.currentUser, 'super-admin') ||
           this.hasRole(this.currentUser, 'Super Admin') || // Also check Azure AD display name format
           this.hasRole(this.currentUser, 'askaldo-admin') ||
           this.hasRole(this.currentUser, 'content-manager') ||
           this.hasRole(this.currentUser, 'moderator') ||
           this.hasRole(this.currentUser, 'manager') ||
           this.hasRole(this.currentUser, 'tech-support') ||
           this.hasRole(this.currentUser, 'policy_testing_admin') ||
           this.hasRole(this.currentUser, 'pwms_testing_admin');
    
    console.log('🔧 [Auth] hasAdminAccess check:', {
      userRoles: this.currentUser.userRoles,
      hasTraditionalRole,
      hasAdmin: this.hasRole(this.currentUser, 'admin'),
      hasSuperAdmin: this.hasRole(this.currentUser, 'super-admin'),
      hasSuperAdminAlt: this.hasRole(this.currentUser, 'Super Admin')
    });
    
    if (hasTraditionalRole) return true;
    
    // Check for agent-specific admin roles
    const agentAdminRoles = [
      'aldo-admin',
      'hr-admin', 
      'hrpolicy-admin',
      'pwms-admin',
      'product-admin',
      'orientation-admin',
      'owensboro2-admin',
      'unfdocs-admin',
      'iris-admin'
    ];
    
    const hasAgentAdminRole = agentAdminRoles.some(role => 
      this.hasRole(this.currentUser!, role)
    );
    
    if (hasAgentAdminRole) return true;
    
    // Check RBAC permissions (cached from last API call)
    return this.cachedRBACPermissions?.hasAdminAccess || false;
  }

  // Check if current user has full admin access (excludes testing-only roles AND agent-specific roles)
  hasFullAdminAccess(): boolean {
    if (!this.currentUser) return false;
    
    // Full admin roles - NOT including agent-specific admin roles like hr-admin, pwms-admin, etc.
    // Agent-specific admins should only see their own agent's dashboard
    const hasFullAdminRole = this.hasRole(this.currentUser, 'admin') || 
           this.hasRole(this.currentUser, 'super-admin') ||
           this.hasRole(this.currentUser, 'askaldo-admin') ||
           this.hasRole(this.currentUser, 'content-manager') ||
           this.hasRole(this.currentUser, 'moderator') ||
           this.hasRole(this.currentUser, 'manager') ||
           this.hasRole(this.currentUser, 'tech-support');
    
    if (hasFullAdminRole) return true;
    
    // Check RBAC permissions (cached from last API call)
    return this.cachedRBACPermissions?.hasFullAdminAccess || false;
  }

  // Check if current user is a testing admin (limited access)
  isTestingAdmin(): boolean {
    if (!this.currentUser) return false;
    
    return this.hasRole(this.currentUser, 'policy_testing_admin') ||
           this.hasRole(this.currentUser, 'pwms_testing_admin');
  }

  // Check if current user can access policy testing features
  canAccessPolicyTesting(): boolean {
    if (!this.currentUser) return false;
    
    // Full admins or policy testing admins can access policy testing
    return this.hasFullAdminAccess() ||
           this.hasRole(this.currentUser, 'policy_testing_admin');
  }

  // Check if current user can access PWMS testing features
  canAccessPWMSTesting(): boolean {
    if (!this.currentUser) return false;
    
    // Full admins or PWMS testing admins can access PWMS testing
    return this.hasFullAdminAccess() ||
           this.hasRole(this.currentUser, 'pwms_testing_admin');
  }

  // Check if current user has specific admin level
  isSuperAdmin(): boolean {
    return this.hasRole(this.currentUser!, 'super-admin');
  }

  // Maps each agent ID (matching apps/express-api/config/agents.json) to the set
  // of Entra role names that grant agent-specific admin access for that agent.
  // Mirrors the backend mapping in apps/express-api/middleware/agentAccessControl.js.
  private static AGENT_ADMIN_ROLE_MAP: Record<string, string[]> = {
    'aldo':              ['aldo-admin'],
    'hrpolicy':          ['hr-admin', 'hrpolicy-admin'],
    'pwms-support':      ['pwms-admin'],
    'product':           ['product-admin'],
    'orientation':       ['orientation-admin'],
    'direct_embroidery': ['owensboro2-admin'],
    'unifirst_docs':     ['unfdocs-admin'],
    'firstaid-safety':   ['firstaid-admin', 'firstaid-safety-admin'],
    'iris':              ['iris-admin'],
  };

  // "Super-admin tier" roles — these grant admin access to ALL agents and the
  // platform itself. Kept in sync with ENTRA_SUPER_ADMIN_ROLES in
  // apps/express-api/middleware/agentAccessControl.js.
  private static SUPER_ADMIN_TIER_ROLES = ['super-admin', 'askaldo-admin', 'admin', 'Super Admin'];

  // Check if the current user is an admin for a specific agent. Returns true
  // when the user holds a super-admin-tier role (admin for everything) or
  // holds an agent-specific admin role from AGENT_ADMIN_ROLE_MAP.
  isAgentAdmin(agentId: string): boolean {
    if (!this.currentUser) return false;
    if (this.hasAnyRole(AuthService.SUPER_ADMIN_TIER_ROLES)) return true;
    const agentRoles = AuthService.AGENT_ADMIN_ROLE_MAP[agentId];
    if (!agentRoles || agentRoles.length === 0) return false;
    return this.hasAnyRole(agentRoles);
  }

  // Check if current user is an employee (useful for internal features)
  isEmployee(): boolean {
    return this.hasRole(this.currentUser!, 'employee');
  }

  // Get all user roles as array
  getUserRoles(): string[] {
    return this.currentUser?.userRoles || [];
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles: string[]): boolean {
    if (!this.currentUser) return false;
    return roles.some(role => this.hasRole(this.currentUser!, role));
  }

  // Toggle mock admin mode (development only)
  toggleMockAdminMode(): boolean {
    if (!this.isLocalDevelopment()) {
      console.warn('Mock admin mode only available in development');
      return false;
    }
    
    // Admin mode is now default (true), can be disabled by setting to 'false'
    const currentMode = localStorage.getItem('mockAdminMode') !== 'false';
    const newMode = !currentMode;
    localStorage.setItem('mockAdminMode', newMode ? 'true' : 'false');
    
    return newMode;
  }

  // Login with OIDC (UniFirst SSO)
  login(provider?: 'oidc', returnUrl?: string): void {
    // If running locally, just reload the page to trigger mock authentication
    if (this.isLocalDevelopment()) {
      this.currentUser = this.getMockUser();
      window.location.reload();
      return;
    }

    // Store return URL for after login
    if (returnUrl) {
      sessionStorage.setItem('oidc_return_url', returnUrl);
    }

    // Always use OIDC for UniFirst SSO
    this.loginWithOidc();
  }

  // Login with OIDC provider
  private async loginWithOidc(): Promise<void> {
    try {
      await this.oidcService.login();
    } catch (error) {
      console.error('OIDC login failed:', error);
    }
  }


  // Logout
  logout(returnUrl?: string): void {
    // Clear all cached data before logout
    this.clearAllAuthData();
    
    // If running locally, just clear the mock user and reload
    if (this.isLocalDevelopment()) {
      this.clearCachedUser();
      window.location.reload();
      return;
    }

    // Send message to service worker to clear caches
    this.clearServiceWorkerCache();

    // Always use OIDC logout
    this.logoutOidc();
  }

  // Logout with OIDC provider
  private async logoutOidc(): Promise<void> {
    try {
      await this.oidcService.logout();
    } catch (error) {
      console.error('OIDC logout failed:', error);
    }
  }


  // Clear all authentication-related data
  private clearAllAuthData(): void {
    try {
      // Clear localStorage
      localStorage.removeItem('mockAdminMode');
      localStorage.removeItem('userSession');
      localStorage.removeItem('chatHistory');
      
      // Clear sessionStorage
      sessionStorage.clear();
      
      // Clear current user and RBAC cache
      this.currentUser = null;
      this.cachedRBACPermissions = null;
      this.rbacPermissionsExpiry = 0;
    } catch (error) {
      console.error('Error clearing auth data:', error);
    }
  }

  // DEPRECATED: Fetch and cache RBAC permissions from the API
  // All permissions now come from JWT roles in user.userRoles
  private async fetchRBACPermissions(): Promise<RBACPermissions | null> {
    // No-op: RBAC permissions are now extracted from JWT roles
    // This method kept for backward compatibility but does nothing
    console.warn('fetchRBACPermissions is deprecated - using JWT roles instead');
    return null;
  }

  // DEPRECATED: Get RBAC permissions (with caching)
  // All permissions now come from JWT roles in user.userRoles
  private async getRBACPermissions(): Promise<RBACPermissions | null> {
    // No-op: RBAC permissions are now extracted from JWT roles
    return null;
  }

  // DEPRECATED: Refresh RBAC permissions cache
  // All permissions now come from JWT roles in user.userRoles
  async refreshRBACPermissions(): Promise<void> {
    // No-op: RBAC permissions are now extracted from JWT roles
    console.warn('refreshRBACPermissions is deprecated - JWT roles are always current');
  }

  // Send message to service worker to clear caches
  private clearServiceWorkerCache(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        if (registration.active) {
          registration.active.postMessage({
            type: 'LOGOUT'
          });
        }
      }).catch((error) => {
        console.error('Error communicating with service worker:', error);
      });
    }
  }

  // Get cached user (for immediate access without API call)
  getCachedUser(): AuthUser | null {
    return this.currentUser;
  }

  // Clear cached user
  clearCachedUser(): void {
    this.currentUser = null;
  }
}

export default AuthService;